import { useState } from "react";
import { resolveAgencyKey } from "@/lib/agency";

export interface ActiveTrip {
  tripId: string;
  /** Agency Key owning this trip. Sent on the trip-updates call. */
  agency: string;
  userStopId?: string;
  routeShortName?: string;
  headsign?: string;
  startedAt: number; // unix timestamp ms
}

const STORAGE_KEY = "via-la:activeTrip";
const MAX_AGE_MS = 3 * 60 * 60 * 1000; // 3 hours

function readFromStorage(): ActiveTrip | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    // Trips saved before the Agency Key existed have no `agency`, and ones
    // saved before the bus-feed rename store `lametro` (see docs/adr/0006).
    const parsed = JSON.parse(stored) as Omit<ActiveTrip, "agency"> & {
      agency?: string;
    };
    if (Date.now() - parsed.startedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { ...parsed, agency: resolveAgencyKey(parsed.agency) };
  } catch {
    return null;
  }
}

export function useActiveTrip() {
  const [activeTrip, setActiveTripState] = useState<ActiveTrip | null>(
    readFromStorage,
  );

  const setActiveTrip = (trip: ActiveTrip) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trip));
    } catch {
      // localStorage unavailable (SSR / private mode)
    }
    setActiveTripState(trip);
  };

  const clearActiveTrip = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
    setActiveTripState(null);
  };

  return { activeTrip, setActiveTrip, clearActiveTrip };
}
